/**
 * ShieldWall — Configuration & Types
 * @module config
 */

// ─── Configuration ───────────────────────────────────────────────────────────

export interface ShieldWallConfig {
  /** Base URL for the captcha API endpoints */
  apiBase: string;

  /** Action context (login, signup, payment, form_submit, coupon_redeem, generic) */
  action?: string;

  /** Difficulty tiers (hex zeros required in PoW hash) */
  difficultyTiers: {
    LOW: number;
    MEDIUM: number;
    HIGH: number;
    EXTREME: number;
  };

  /** Score thresholds */
  scoreThresholds: {
    pass: number;
    challenge: number;
  };

  /** PoW configuration */
  pow: {
    memoryBlocks: number;
    maxSolveTimeMs: number;
    yieldInterval: number;
  };

  /** Challenge TTL in seconds */
  challengeTtlSeconds: number;
}

export const DEFAULT_CONFIG: ShieldWallConfig = {
  apiBase: "/api/captcha",
  difficultyTiers: { LOW: 3, MEDIUM: 4, HIGH: 5, EXTREME: 6 },
  scoreThresholds: { pass: 70, challenge: 40 },
  // 60s base → 180s hard deadline (×3 in solveMemoryHardPoW). Live tiers at
  // difficulty=4 average ~2^16 memory-hard attempts (~20–60s on Node; longer
  // in the browser with frame yields).
  pow: { memoryBlocks: 64, maxSolveTimeMs: 60_000, yieldInterval: 5 },
  challengeTtlSeconds: 120,
};

// ─── API Types ───────────────────────────────────────────────────────────────

export interface ChallengeResponse {
  nonce: string;
  timestamp: number;
  difficulty: number;
  tierName: string;
  signature: string;
  challengeSeed: number;
  enabledChallenges: string[];
  reputation: string;
  ttl: number;
  action: string;
  escalationLevel: number;
  serverChallenge?: {
    question: string;
    inputType: string;
    label: string;
    challengeType: string;
  } | null;
  /** v2 canonical — PoW effort mode bound into the server HMAC signature. */
  mode?: "probabilistic" | "deterministic";
  /** v2 canonical — PoW algorithm bound into the server HMAC signature. */
  algo?: "sha256-chain" | "argon2id";
  /** Server's per-challenge RSA-2048 SPKI PEM public key for payload encryption. */
  publicKey?: string | null;
}

export interface VerifyPayload {
  nonce: string;
  timestamp: number;
  difficulty: number;
  challengeSeed: number;
  signature: string;
  powResult: {
    nonce: number;
    hash: string;
    solveTimeMs: number;
    iterations: number;
    memoryBlocks: number;
    type: string;
  };
  behaviorScore: number;
  browserScore: number;
  consistencyPenalty: number;
  honeypotTriggered: boolean;
}

export interface VerifyResponse {
  verdict: "PASSED" | "CHALLENGE_REQUIRED" | "REJECTED";
  score: number;
  breakdown: Record<string, number>;
  errors: string[];
  token: string | null;
  /**
   * Single-use token for `POST /api/v1/siteverify` (~2 min TTL). Present only
   * when the widget bound a `siteKey`. Optional so hosts on older servers keep
   * type-checking.
   */
  responseToken?: string | null;
  /**
   * SW-901 stable reason codes (`docs/REASON-CODES.md`). Additive — treat
   * unknown codes as informational and never branch on absence.
   */
  reasonCodes?: string[];
}

export interface ScoreBreakdown {
  pow: number;
  behavior: number;
  honeypot: number;
  fingerprint: number;
  consistency: number;
  memoryHard: number;
}
